import HorizontalLine from 'common/components/entities/HorizontalLine'
import { ProductInterface } from 'common/types/entities/ProductInterface'
import { PricePlan as PricePlanType } from 'common/types/OfferInterface'
import React from 'react'
import { DigitalProduct } from 'src/shared/components/offer-price/digital-product'
import { OfferLineUiProps } from 'src/shared/components/offer-price/offer-line-item/offer-line-item'
import { OrderTotal } from 'src/shared/components/offer-price/order-total'
import { PhysicalProduct } from 'src/shared/components/offer-price/physical-product'
import { CalculatePriceResponse } from 'src/shared/types/pricing-types'
import {
  isLimitedSubscriptionPlan,
  isSubscriptionPlan,
} from 'src/shared/utils/price-plan-utils'

export interface OfferPriceProps {
  attrId: string
  activePlanId?: number
  plans: PricePlanType[]
  pricePlansPricing: CalculatePriceResponse[]
  bumpPlan?: PricePlanType
  bumpPlanPricing?: CalculatePriceResponse
  mainOfferPricing?: CalculatePriceResponse
  bumpOfferPricing?: CalculatePriceResponse
  offerLineUiProps: OfferLineUiProps
  product?: ProductInterface | null
  productBump?: ProductInterface | null
  total: number
  dueToday?: number
  togglePricePlan?: (id: number) => void
  customDescription?: string
  locale: string
}

export const OfferPrice = ({
  attrId,
  plans,
  bumpPlan,
  mainOfferPricing,
  bumpOfferPricing,
  pricePlansPricing,
  bumpPlanPricing,
  total,
  dueToday,
  activePlanId,
  togglePricePlan,
  customDescription,
  locale,
  product,
  productBump,
  offerLineUiProps,
}: OfferPriceProps) => {
  const isProductBumpSelected = !!productBump?.id
  const isDigitalBumpSelected = !!bumpPlan?.id
  const shouldDisplayOfferTotal = isProductBumpSelected || isDigitalBumpSelected
  const activePlan = plans.find(({ id }) => id === activePlanId)
  const isRecurrentPayment =
    isSubscriptionPlan(activePlan) ||
    isLimitedSubscriptionPlan(activePlan) ||
    isSubscriptionPlan(bumpPlan) ||
    isLimitedSubscriptionPlan(bumpPlan)
  const totalCurrency = mainOfferPricing?.currencyCode ?? activePlan?.currency

  return (
    <div id={attrId} data-test-element="offer-price">
      {pricePlansPricing.map((planPricing, index) => {
        const pricePlan = plans.find(({ id }) => id === planPricing.pricePlan)

        return (
          <DigitalProduct
            isSelected={activePlanId === planPricing.pricePlan}
            onSelect={() => togglePricePlan?.(planPricing.pricePlan)}
            offerPricing={planPricing}
            pricePlan={pricePlan}
            offerLineUiProps={offerLineUiProps}
            locale={locale}
            dataTestIdName={`main-offer-name-digital-${index}`}
            dataTestIdPrice={`main-offer-price-digital-${index}`}
            dataTestIdCurrency={`main-offer-currency-digital-${index}`}
            dataTestIdTax={`main-offer-tax-digital-${index}`}
          />
        )
      })}
      {mainOfferPricing && (
        <PhysicalProduct
          offerPricing={mainOfferPricing}
          product={product}
          offerLineUiProps={offerLineUiProps}
          locale={locale}
          dataTestIdName="main-offer-name-physical"
          dataTestIdPrice="main-offer-price-physical"
          dataTestIdCurrency="main-offer-currency-physical"
          dataTestIdTax="main-offer-tax-physical"
        />
      )}
      {isDigitalBumpSelected && (
        <>
          <HorizontalLine style={{ marginTop: 20 }} />
          <DigitalProduct
            isSelected
            offerPricing={bumpPlanPricing}
            pricePlan={bumpPlan}
            offerLineUiProps={offerLineUiProps}
            locale={locale}
            dataTestIdName="bump-offer-name-digital"
            dataTestIdPrice="bump-offer-price-digital"
            dataTestIdCurrency="bump-offer-currency-digital"
            dataTestIdTax="bump-offer-tax-digital"
          />
        </>
      )}
      {isProductBumpSelected && (
        <>
          <HorizontalLine style={{ marginTop: 20 }} />
          <PhysicalProduct
            offerPricing={bumpOfferPricing}
            product={productBump}
            offerLineUiProps={offerLineUiProps}
            locale={locale}
            dataTestIdName="bump-offer-name-physical"
            dataTestIdPrice="bump-offer-price-physical"
            dataTestIdCurrency="bump-offer-currency-physical"
            dataTestIdTax="bump-offer-tax-physical"
          />
        </>
      )}
      {shouldDisplayOfferTotal && totalCurrency && (
        <>
          <HorizontalLine style={{ marginTop: 20 }} />
          <OrderTotal
            total={total}
            dueToday={dueToday}
            isRecurrentPayment={isRecurrentPayment}
            offerLineUiProps={offerLineUiProps}
            locale={locale}
            currency={totalCurrency}
            dataTestIdName="offer-total-name"
            dataTestIdPrice="offer-total-name-price"
            dataTestIdCurrency="offer-total-currency"
            dataTestIdTax="offer-total-tax"
          />
        </>
      )}
    </div>
  )
}
